import { findAll, find, prefersReducedMotion } from "./dom";

/**
 * Vågrätt rullande rader — "andra boenden" på boendesidan och bildraden på
 * innehållssidorna.
 *
 * <p>Rullningen är CSS:ens, inte JavaScriptets: raden fungerar med finger,
 * styrplatta och tabbtangent utan att en rad kod körs. Det här lägger bara
 * till pilarna för den som styr med mus, och de skapas härifrån just för att
 * de inte ska sitta där döda när JavaScript uteblir.</p>
 */
function scrollStep(rail: HTMLElement): number {
  // En knapptryckning flyttar nästan en hel synlig bredd — tillräckligt för
  // att något nytt ska komma fram, men med ett kort kvar som hållpunkt.
  return rail.clientWidth * 0.85;
}

function icon(path: string): string {
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"
    stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
}

export function initRails(): void {
  for (const rail of findAll("[data-rail]")) {
    const head = find("[data-rail-head]", rail.parentElement ?? document);
    if (head === null) {
      continue;
    }

    // Etiketterna star i markupen: raden bar boenden pa en sida och bilder pa
    // en annan, och "Visa fler boenden" over ett brollopsfoto ar fel besked
    // till den som lyssnar sig fram.
    const back = rail.dataset["railBackLabel"] ?? "Visa föregående boenden";
    const forward = rail.dataset["railForwardLabel"] ?? "Visa fler boenden";

    const controls = document.createElement("div");
    controls.className = "rail__controls";
    controls.innerHTML = `
      <button class="rail__btn" type="button" data-back aria-label="${back}">
        ${icon('<path d="M15 5l-7 7 7 7"/>')}
      </button>
      <button class="rail__btn" type="button" data-forward aria-label="${forward}">
        ${icon('<path d="M9 5l7 7-7 7"/>')}
      </button>`;
    head.append(controls);

    const backButton = find<HTMLButtonElement>("[data-back]", controls);
    const forwardButton = find<HTMLButtonElement>("[data-forward]", controls);
    if (backButton === null || forwardButton === null) {
      continue;
    }

    const move = (direction: number): void => {
      rail.scrollBy({
        left: direction * scrollStep(rail),
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    };
    backButton.addEventListener("click", () => move(-1));
    forwardButton.addEventListener("click", () => move(1));

    // Knapparna speglar var raden står: den som redan är längst ut ska se
    // att det inte finns mer åt det hållet.
    const sync = (): void => {
      const slack = rail.scrollWidth - rail.clientWidth;
      controls.hidden = slack <= 1;
      // Marginalen ar for delpixlar: en rad som star still hamnar sallan pa
      // exakt noll, och en knapp som ser aktiv ut men inte gor nagot ar varre
      // an en som ar slackt.
      const atStart = rail.scrollLeft <= 2;
      const atEnd = rail.scrollLeft >= slack - 2;
      backButton.disabled = atStart;
      forwardButton.disabled = atEnd;

      // Samma besked en gang till, for ogat: kanten tonar bort at det hall
      // raden fortsatter. Klassnamnen ar radens, inte boendekortens — samma
      // toning galler bildraden.
      rail.classList.toggle("rail--fade-start", !atStart);
      rail.classList.toggle("rail--fade-end", slack > 1 && !atEnd);
    };
    sync();

    rail.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync, { passive: true });
  }
}
